"use client";

import React, { useEffect, useRef, useState } from "react";
import { Area, AreaChart, CartesianGrid, XAxis, YAxis, ResponsiveContainer } from "recharts";
import { ShieldCheck, Activity, Database, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ChartContainer, ChartTooltip, ChartTooltipContent, ChartLegend, ChartLegendContent } from "@/components/ui/chart";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = '0px'
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (typeof window === 'undefined' || typeof IntersectionObserver === 'undefined') {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) observer.unobserve(entry.target);
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const chartData = [{
  month: "2019",
  clientSide: 85,
  serverSide: 90
}, {
  month: "2020",
  clientSide: 88,
  serverSide: 92
}, {
  month: "2021",
  clientSide: 92,
  serverSide: 95
}, {
  month: "2022",
  clientSide: 95,
  serverSide: 98
}, {
  month: "2023",
  clientSide: 97,
  serverSide: 99
}, {
  month: "2024",
  clientSide: 99,
  serverSide: 100
}];
const chartConfig = {
  serverSide: {
    label: "Waterproofing Integrity",
    color: "var(--primary)",
    icon: Database
  },
  clientSide: {
    label: "Industry Standard",
    color: "var(--muted-foreground)",
    icon: Activity
  }
};
const stats = [{
  value: "25+ Years",
  label: "Industry Experience",
  description: "A quarter-century of structural protection and roofing excellence."
}, {
  value: "1,500+",
  label: "Projects Completed",
  description: "Successful installations across residential and industrial sectors."
}, {
  value: "100%",
  label: "Waterproofing Success",
  description: "Zero-leak guarantee through our advanced multi-layer sealing."
}];
const hexStream = ["ROOFING WATERPROOFING INSULATION PROTECTION DURABILITY", "RM ROOFING PRECISION ENGINEERING INDUSTRIAL STRENGTH", "SAFETY AMBER SLATE NAVY ARCHITECTURAL INTEGRITY", "WEATHERPROOFING SEALANT MEMBRANE REPAIR MAINTENANCE", "CERTIFIED CRAFTSMANSHIP QUALITY CONTROL INSPECTION", "RESIDENTIAL COMMERCIAL INDUSTRIAL SOLUTIONS 2024", "ESTABLISHED RELIABILITY TRUSTED PARTNER BUILDING", "SABS APPROVED ISO CERTIFIED GUARANTEED RESULTS"];
export default function TheFirstPartyAdvantage() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll();
  const {
    ref: chartRef,
    isVisible: isChartVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: statsRef,
    isVisible: isStatsVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="223816" id="proven-reliability" className="dark bg-background text-foreground relative overflow-hidden py-24 lg:py-32">
            {}
            <div className="absolute inset-0 pointer-events-none overflow-hidden opacity-[0.05] flex flex-col justify-center z-0" aria-hidden="true">
                {Array.from({
        length: 20
      }).map((_, i) => <div key={i} data-index={i} className="whitespace-nowrap font-mono text-xs tracking-widest text-primary" style={{
        transform: `translateX(${i % 2 === 0 ? '-10%' : '0%'})`,
        marginLeft: `${i * 15 % 100}px`
      }}>
                        {hexStream.join(" ")} {hexStream.join(" ")}
                    </div>)}
            </div>

            {}
            <div className="absolute top-1/4 left-0 w-[500px] h-[500px] bg-primary/5 rounded-full blur-[120px] -translate-x-1/2 pointer-events-none z-0" />
            <div className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-chart-2/5 rounded-full blur-[150px] translate-x-1/3 translate-y-1/3 pointer-events-none z-0" />

            <div className="container relative z-10 mx-auto px-4 md:px-6">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
                    
                    {}
                    <div className="lg:col-span-5 flex flex-col gap-8">
                        <div ref={headerRef} className={`transition-all duration-700 ease-out ${isHeaderVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
                            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-primary text-sm font-medium mb-6">
                                <ShieldCheck className="w-4 h-4" />
                                <span>50+ Industry Certifications</span>
                            </div>
                            <h2 className="text-4xl md:text-5xl font-bold font-default leading-tight mb-6 tracking-tighter">
                                Proven <br />
                                <span className="bg-gradient-to-r from-primary to-chart-2 bg-clip-text text-transparent drop-shadow-sm">
                                    Reliability
                                </span>
                            </h2>
                            <p className="text-lg text-muted-foreground leading-relaxed font-default">
                                RM Roofing & Waterproofing delivers architectural precision in every layer. Our integrated solutions protect your assets from the harshest elements, ensuring long-term structural durability through expert craftsmanship and industrial-grade materials.
                            </p>
                        </div>

                        <div ref={statsRef} className={`grid gap-6 transition-all duration-700 delay-200 ease-out ${isStatsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
                            {stats.map((stat, i) => <div key={i} data-index={i} className="flex flex-col gap-1 border-l-2 border-primary/30 pl-4 hover:border-primary transition-colors">
                                    <span className="text-4xl font-bold font-mono bg-gradient-to-br from-primary to-chart-3 bg-clip-text text-transparent">
                                        {stat.value}
                                    </span>
                                    <span className="text-foreground font-semibold font-default text-lg">
                                        {stat.label}
                                    </span>
                                    <span className="text-muted-foreground text-sm font-default">
                                        {stat.description}
                                    </span>
                                </div>)}
                        </div>

                        </div>

                    {}
                    <div ref={chartRef} className={`lg:col-span-7 transition-all duration-1000 delay-300 ease-out ${isChartVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'}`}>
                        <Card className="bg-card/40 backdrop-blur-md border-border/50 shadow-2xl shadow-black/20 relative overflow-hidden rounded-lg">
                            {}
                            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary/50 via-chart-2/50 to-primary/50" />
                            
                            <CardHeader className="pb-8">
                                <CardTitle className="text-xl font-default flex items-center gap-2">
                                    <Activity className="w-5 h-5 text-primary" />
                                    Performance Metrics: Protection Standards
                                </CardTitle>
                                <CardDescription className="text-base font-default">
                                    Comparing RM Waterproofing integrity against standard industry benchmarks.
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                                <ChartContainer config={chartConfig} className="h-[350px] w-full">
                                    <AreaChart data={chartData} margin={{
                  top: 10,
                  right: 10,
                  left: -20,
                  bottom: 0
                }}>
                                        <defs>
                                            <linearGradient id="fillServer" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor="var(--color-serverSide)" stopOpacity={0.3} />
                                                <stop offset="95%" stopColor="var(--color-serverSide)" stopOpacity={0.0} />
                                            </linearGradient>
                                            <linearGradient id="fillClient" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor="var(--color-clientSide)" stopOpacity={0.3} />
                                                <stop offset="95%" stopColor="var(--color-clientSide)" stopOpacity={0.0} />
                                            </linearGradient>
                                        </defs>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border)" opacity={0.2} />
                                        <XAxis dataKey="month" tickLine={false} axisLine={false} tickMargin={12} tickFormatter={value => value} stroke="var(--muted-foreground)" className="font-mono text-[10px]" />
                                        <YAxis tickLine={false} axisLine={false} tickMargin={12} tickFormatter={value => `${value}%`} stroke="var(--muted-foreground)" className="font-mono text-[10px]" />
                                        <ChartTooltip cursor={{
                    stroke: 'var(--border)',
                    strokeWidth: 1,
                    strokeDasharray: '4 4'
                  }} content={<ChartTooltipContent indicator="dot" />} />
                                        <Area type="monotone" dataKey="serverSide" stroke="var(--color-serverSide)" strokeWidth={3} fill="url(#fillServer)" activeDot={{
                    r: 6,
                    fill: "var(--color-serverSide)",
                    stroke: "var(--background)",
                    strokeWidth: 2
                  }} />
                                        <Area type="monotone" dataKey="clientSide" stroke="var(--color-clientSide)" strokeWidth={2} strokeDasharray="5 5" fill="url(#fillClient)" activeDot={{
                    r: 6,
                    fill: "var(--color-clientSide)",
                    stroke: "var(--background)",
                    strokeWidth: 2
                  }} />
                                        <ChartLegend content={<ChartLegendContent />} className="pt-6 font-default" />
                                    </AreaChart>
                                </ChartContainer>
                            </CardContent>
                        </Card>

                        {}
                        <div className="absolute -right-4 top-1/4 bg-card/80 backdrop-blur-sm border border-border p-3 rounded-lg shadow-lg animate-pulse hidden md:block">
                            <div className="flex items-center gap-2">
                                <div className="w-2 h-2 rounded-full bg-chart-2 animate-ping" />
                                <span className="text-xs font-mono text-muted-foreground">SEAL INTEGRITY: 100%</span>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </section>;
}